import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Users, UserCheck } from 'lucide-react';

interface ActiveUser {
  user_id: string;
  display_name?: string;
  user_type?: string;
  last_seen: string;
}

export default function ActiveUsersTracker() {
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [totalClients, setTotalClients] = useState(0);
  const [totalAdvisors, setTotalAdvisors] = useState(0);

  useEffect(() => {
    fetchActiveUsers();

    const interval = setInterval(() => {
      fetchActiveUsers();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const fetchActiveUsers = async () => {
    try {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();

      const { data: users, error } = await supabase
        .from('user_activity')
        .select(`
          user_id,
          last_seen,
          profiles!inner(display_name, user_type)
        `)
        .gte('last_seen', fiveMinutesAgo)
        .order('last_seen', { ascending: false });

      if (error) {
        console.error('Error fetching active users:', error);
        return;
      }

      if (users) {
        const activeUsersList: ActiveUser[] = users.map((u: any) => ({
          user_id: u.user_id,
          display_name: u.profiles?.display_name || 'Anonymous',
          user_type: u.profiles?.user_type || 'client',
          last_seen: u.last_seen
        }));

        setActiveUsers(activeUsersList);

        const clients = activeUsersList.filter(u => u.user_type === 'client').length;
        const advisors = activeUsersList.filter(u => u.user_type === 'advisor').length;

        setTotalClients(clients);
        setTotalAdvisors(advisors);
      }
    } catch (err) {
      console.error('Error in fetchActiveUsers:', err);
    }
  };

  const totalActive = totalClients + totalAdvisors;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-green-600" />
          Active Users (Last 5 minutes)
        </h3>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-gray-500">Live</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Total</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">{totalActive}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">Clients</span>
          </div>
          <p className="text-3xl font-bold text-purple-600">{totalClients}</p>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-900">Advisors</span>
          </div>
          <p className="text-3xl font-bold text-amber-600">{totalAdvisors}</p>
        </div>
      </div>

      {activeUsers.length > 0 && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent Activity</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {activeUsers.slice(0, 10).map((user, index) => (
              <div key={`${user.user_id}-${index}`} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    user.user_type === 'advisor' ? 'bg-amber-500' : 'bg-purple-500'
                  }`}></div>
                  <span className="text-gray-900">{user.display_name}</span>
                  <span className="text-xs text-gray-500 capitalize">
                    ({user.user_type})
                  </span>
                </div>
                <span className="text-xs text-gray-400">
                  {new Date(user.last_seen).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeUsers.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No active users in the last 5 minutes</p>
        </div>
      )}
    </div>
  );
}

function Activity({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      strokeWidth="2"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}
